"use client";

/**
 * NewsMock — animated mock of the askFinz News "The Brief" front page.
 *
 * Layout mirrors the real news.askfinz.ai home:
 *   - Breaking strip at top
 *   - Ticker row (trending headlines scrolling)
 *   - Page head: date + "The Brief"
 *   - AI Briefing hero with waveform player
 *   - 2-column section: lead headline (left) + trending sidebar (right)
 *   - Ranked story list below lead
 *   - Per-source angle expansion (scripted)
 *
 * Animation loop:
 *   1. Waveform plays itself forward
 *   2. Lead story expands to show per-source angles
 *   3. Reset and repeat
 *
 * No network calls. All data hard-coded.
 */
import { useEffect, useRef, useState } from "react";
import {
  Headphones,
  Play,
  Pause,
  TrendingUp,
  ChevronDown,
  ArrowUpRight,
  Zap,
} from "lucide-react";

/* ── static data ── */
const BREAKING = "ECB holds rates steady at 4.0% — Lagarde: 'data not yet sufficient for pivot'";

const TICKER_ITEMS = [
  "NVDA Q2 results beat estimates; shares up 6% in pre-market",
  "UK CPI falls to 2.1% — Bank of England decision due Thursday",
  "OpenAI announces o3-mini successor with 200K context window",
  "China PMI rises to 51.4, first expansion in four months",
  "Tesla Cybertruck recall expanded to cover 46,000 vehicles",
];

const LEAD = {
  category: "Markets",
  kicker: "LIVE · developing",
  title: "Federal Reserve signals one cut in 2026 as inflation descent stalls",
  description:
    "Minutes from the May FOMC meeting show committee members aligned on patience — the labour market remains too tight and shelter costs too sticky to justify moving rates before September at the earliest.",
  byline: "askFinz Newsroom",
  time: "2h ago",
  sources: [
    { outlet: "reuters.com",    angle: "Minutes summary — patient stance confirmed" },
    { outlet: "ft.com",         angle: "Curve flattening — two-year reaction" },
    { outlet: "wsj.com",        angle: "Rate-watcher poll: September now base case" },
    { outlet: "bloomberg.com",  angle: "Fed-watcher: shelter CPI the swing factor" },
    { outlet: "marketwatch.com", angle: "Equity desks re-pricing rate sensitivity" },
  ],
};

const RANKED = [
  { n: "02", cat: "Tech",      title: "EU AI Act tier-2 obligations draft lands — foundation model carve-outs intact" },
  { n: "03", cat: "Business",  title: "Apple closes $5B Arm stake as custom silicon roadmap accelerates" },
  { n: "04", cat: "Climate",   title: "EU CBAM expansion to absorb steel, fertilisers and aluminium by Q3 2026" },
  { n: "05", cat: "Politics",  title: "UK general election date confirmed for 14 November after budget vote" },
];

const TRENDING = [
  { tag: "#FedMinutes",  vel: "+820%", title: "Fed holds — one cut in 2026" },
  { tag: "#EUAIAct",     vel: "+640%", title: "Risk-tier 2 obligations draft drops" },
  { tag: "#NVDA",        vel: "+510%", title: "NVDA beats Q2 estimates" },
  { tag: "#CBAM",        vel: "+390%", title: "EU carbon border tax expands" },
  { tag: "#AppleArm",    vel: "+280%", title: "Apple closes Arm stake" },
];

/* Synthetic waveform — 56 bars */
const WAVE = [
  14, 22, 18, 32, 26, 40, 30, 20, 36, 24, 28, 16, 34, 42, 22, 18, 30, 44,
  20, 14, 26, 38, 18, 22, 32, 28, 16, 34, 40, 22, 18, 28, 24, 36, 30, 14,
  26, 34, 20, 24, 32, 22, 16, 30, 38, 20, 18, 28, 14, 24, 32, 20, 16, 26,
  18, 30,
];

export function NewsMock() {
  const [playing, setPlaying] = useState(false);
  const [waveProgress, setWaveProgress] = useState(0);
  const [anglesOpen, setAnglesOpen] = useState(false);
  const [activeAngle, setActiveAngle] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const clearTs = () => { timers.current.forEach(clearTimeout); timers.current = []; };
    const sched = (fn: () => void, d: number) => { const id = window.setTimeout(fn, d); timers.current.push(id); return id; };

    const runCycle = () => {
      setPlaying(false);
      setWaveProgress(0);
      setAnglesOpen(false);
      setActiveAngle(0);

      // 1. Play the waveform
      sched(() => setPlaying(true), 600);
      for (let i = 1; i <= 36; i++) {
        sched(() => setWaveProgress(i / 36), 600 + i * 90);
      }
      sched(() => setPlaying(false), 600 + 36 * 90 + 200);

      // 2. Open source angles on the lead story
      sched(() => setAnglesOpen(true), 4800);

      // 3. Cycle active angle highlight
      for (let n = 1; n < LEAD.sources.length; n++) {
        sched(() => setActiveAngle(n), 4800 + n * 700);
      }

      // 4. Close angles + loop
      sched(() => {
        setAnglesOpen(false);
        setActiveAngle(0);
        clearTs();
        runCycle();
      }, 4800 + LEAD.sources.length * 700 + 1800);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        sched(runCycle, 400);
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => { obs.disconnect(); clearTs(); };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">

        {/* Browser chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline bg-background/60">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 flex-1 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            news.askfinz.ai
          </span>
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Live
          </span>
        </div>

        {/* Breaking strip */}
        <div className="flex items-center gap-3 px-4 py-2 bg-foreground text-background text-[11px] font-mono overflow-hidden">
          <span className="inline-flex items-center gap-1.5 shrink-0 font-bold tracking-[0.14em] uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" />
            Breaking
          </span>
          <span className="truncate opacity-90">{BREAKING}</span>
          <span className="ml-auto shrink-0 opacity-60">developing →</span>
        </div>

        {/* Ticker */}
        <div className="flex items-center gap-3 px-4 py-1.5 border-b hairline bg-foreground/[0.02] overflow-hidden">
          <span className="inline-flex items-center gap-1 text-[9.5px] tracking-[0.16em] uppercase text-foreground/65 font-mono shrink-0">
            <TrendingUp className="h-2.5 w-2.5" />
            Trending
          </span>
          <div className="overflow-hidden flex-1">
            <div
              className="flex gap-4 text-[11px] font-mono text-foreground/65 whitespace-nowrap animate-[marquee-x_22s_linear_infinite]"
              style={{ width: "max-content" }}
            >
              {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
                <span key={i}>{t}<span className="mx-2 opacity-40">·</span></span>
              ))}
            </div>
          </div>
        </div>

        {/* Page head */}
        <div className="px-5 pt-4 pb-3 border-b hairline">
          <div className="text-[10px] font-mono tracking-[0.16em] uppercase text-foreground/50">
            {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <div className="mt-0.5 text-[22px] font-bold tracking-tight text-foreground/90">
            The <em className="font-serif not-italic">Brief</em>
          </div>
          <div className="text-[11px] text-foreground/55 mt-0.5">
            Everything moving markets and the world today, read and ranked for you.
          </div>
        </div>

        {/* AI Briefing hero */}
        <div className="px-5 py-4 border-b hairline bg-foreground/[0.015]">
          <div className="flex items-center gap-3 mb-3">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-background shrink-0">
              <Headphones className="h-4 w-4" />
            </span>
            <div>
              <div className="text-[11px] font-mono tracking-[0.14em] uppercase text-foreground/65">
                AI Briefing · Podcast
              </div>
              <div className="text-[13px] font-semibold tracking-tight text-foreground/90">
                Today's top stories, <em className="font-serif not-italic">in a few minutes.</em>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className={
                "ml-auto inline-flex h-9 w-9 items-center justify-center rounded-full shrink-0 transition " +
                (playing
                  ? "bg-[hsl(var(--accent))] text-background"
                  : "bg-foreground text-background hover:opacity-90")
              }
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 translate-x-[1px]" />}
            </button>
          </div>
          {/* waveform */}
          <div className="flex items-center gap-[2px] h-7 mb-1">
            {WAVE.map((h, i) => {
              const pct = (i + 1) / WAVE.length;
              const played = pct <= waveProgress;
              return (
                <span
                  key={i}
                  className={
                    "inline-block w-[3px] rounded-full transition-colors duration-150 " +
                    (played ? "bg-[hsl(var(--accent))]" : "bg-foreground/20")
                  }
                  style={{ height: `${h}px` }}
                />
              );
            })}
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono text-foreground/45">
            <span>{playing ? `${Math.round(waveProgress * 6)}:${String(Math.round((waveProgress * 6 * 60) % 60)).padStart(2, "0")}` : "0:00"}</span>
            <span>6:00</span>
          </div>
        </div>

        {/* Main content area */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {/* Lead + ranked */}
          <div className="md:col-span-2 border-r hairline px-5 py-4 space-y-4">
            {/* lead */}
            <article>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 text-[9.5px] font-bold tracking-[0.16em] uppercase text-red-500 font-mono">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                  {LEAD.kicker}
                </span>
                <span className="text-[9.5px] tracking-[0.14em] uppercase text-foreground/50 font-mono">{LEAD.category}</span>
              </div>
              <h2 className="text-[15px] font-bold leading-snug text-foreground/90 mb-1.5">
                {LEAD.title}
              </h2>
              <p className="text-[12px] text-foreground/65 leading-relaxed mb-2">
                {LEAD.description}
              </p>
              <div className="text-[10px] font-mono text-foreground/45 mb-3">
                {LEAD.byline} · {LEAD.time}
              </div>

              {/* Source angles expansion */}
              <button
                type="button"
                onClick={() => setAnglesOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-foreground/65 font-mono hover:text-foreground transition"
              >
                <ChevronDown className={"h-3 w-3 transition-transform " + (anglesOpen ? "rotate-180" : "")} />
                {LEAD.sources.length} source angles
              </button>
              {anglesOpen && (
                <div className="mt-2 space-y-1 animate-[blur-fade-in_0.4s_ease-out]">
                  {LEAD.sources.map((s, i) => (
                    <div
                      key={s.outlet}
                      className={
                        "flex items-center gap-2 rounded-lg px-2.5 py-1.5 transition-colors text-[11px] " +
                        (i === activeAngle ? "bg-[hsl(var(--accent))]/8" : "")
                      }
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-foreground/30 shrink-0" />
                      <span className="font-mono text-foreground/75 shrink-0">{s.outlet}</span>
                      <span className="text-foreground/55 truncate">— {s.angle}</span>
                      <ArrowUpRight className="h-2.5 w-2.5 ml-auto text-foreground/30 shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </article>

            {/* ranked list */}
            <div className="border-t hairline pt-4 space-y-2.5">
              {RANKED.map((r) => (
                <div key={r.n} className="flex items-start gap-3">
                  <span className="text-[14px] font-bold text-foreground/20 font-mono shrink-0 w-6 text-right leading-tight">{r.n}</span>
                  <div className="min-w-0 flex-1">
                    <span className="text-[9.5px] tracking-[0.14em] uppercase text-foreground/45 font-mono">{r.cat}</span>
                    <div className="text-[12px] text-foreground/80 leading-snug mt-0.5">{r.title}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trending sidebar */}
          <div className="px-4 py-4">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
              <TrendingUp className="h-3 w-3" />
              Trending now
            </div>
            <div className="space-y-3">
              {TRENDING.map((t, i) => (
                <div key={t.tag} className="flex items-start gap-2.5">
                  <span className="text-[12px] font-bold text-foreground/20 font-mono shrink-0 w-4 text-right leading-tight">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold text-foreground/80 font-mono">{t.tag}</div>
                    <div className="text-[10.5px] text-foreground/55 truncate mt-0.5 leading-snug">{t.title}</div>
                    <div className="text-[9.5px] text-[hsl(var(--accent))] font-mono mt-0.5">{t.vel}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t hairline">
              <div className="inline-flex items-center gap-1.5 text-[9.5px] tracking-[0.14em] uppercase text-foreground/50 font-mono">
                <Zap className="h-2.5 w-2.5" />
                Auto-refresh · live
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>
          Live product at <span className="font-mono text-foreground/65">news.askfinz.ai</span>
        </span>
      </div>
    </div>
  );
}

export default NewsMock;
